import { useContext } from "react";

import { ArrowLeft, ArrowRight, Check, ChevronDown, ChevronUp, Mail, Send } from "lucide-react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { UserContext } from "@context/user";
import InputField from "@components/fields/input-field";
import SelectField from "@components/fields/select-field";
import RadioField from "@components/fields/radio-field";
import useBrandRouteGuard from "../use-brand-route-guard";
import useBrandSmtp from "./use-brand-smtp";
import { SECURITY_OPTIONS, SERVER_OPTIONS } from "./schema";

const StepHeader = ({ n, title, open, complete, onClick }) => (
    <button type="button" onClick={onClick} className="flex w-full items-center gap-3 px-5 py-4 text-left">
        <span
            className={classnames(
                "grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-semibold",
                complete && "bg-success text-white",
                !complete && open && "bg-primary text-white",
                !complete && !open && "border border-gray-300 text-muted"
            )}>
            {complete ? <Check className="h-4 w-4" /> : n}
        </span>
        <span className={classnames("text-base font-semibold", !open && !complete ? "text-muted" : "text-gray-900")}>
            {title}
        </span>
        {open ? <ChevronDown className="ml-auto h-4 w-4 text-muted" /> : <ChevronUp className="ml-auto h-4 w-4 text-muted" />}
    </button>
);

StepHeader.propTypes = {
    n       : PropTypes.number.isRequired,
    title   : PropTypes.string.isRequired,
    open    : PropTypes.bool.isRequired,
    complete: PropTypes.bool.isRequired,
    onClick : PropTypes.func.isRequired
};

const BrandSmtp = () => {
    const { brandId } = useBrandRouteGuard();
    const { brands } = useContext(UserContext);
    const brandName = brands.find((b) => b.id === brandId)?.name;

    const {
        control, errors, option, oauthConnected, step, done, submitted,
        setValue, connectOauth, goToStep, next, previous, submit
    } = useBrandSmtp(brandId);

    return (
        <div className="p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">Configure SMTP</h1>
                    <p className="text-sm text-muted">
                        SMTP is used to send debit notes, KYC reminders and payout notifications from
                        {brandName ? ` ${brandName}'s` : " your"} own domain.
                    </p>
                </div>
                <span className={classnames(
                    "rounded-full border px-2.5 py-1 text-xs font-medium",
                    submitted ? "border-success/30 bg-success/10 text-success" : "border-warning/30 bg-warning/15 text-warning-foreground"
                )}>
                    {submitted ? "SMTP configured" : "Not configured"}
                </span>
            </div>

            <form onSubmit={(e) => e.preventDefault()} className="mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <StepHeader n={1} title="Server Selection" open={step === 1} complete={done.includes(1)} onClick={() => goToStep(1)} />
                {step === 1 && (
                    <div className="border-t border-gray-100 bg-light p-5">
                        <p className="mb-3 text-sm font-medium text-gray-700">Server Option</p>
                        <div className="flex flex-wrap gap-3">
                            {SERVER_OPTIONS.map((opt) => (
                                <RadioField
                                    key={opt.value}
                                    control={control}
                                    name="option"
                                    id={opt.value}
                                    label={opt.label}
                                    className="mb-0"
                                    customOnChange={() => setValue("oauth_connected", false)} />
                            ))}
                        </div>
                    </div>
                )}

                <div className="border-t border-gray-100">
                    <StepHeader n={2} title="Server Configuration" open={step === 2} complete={done.includes(2)} onClick={() => goToStep(2)} />
                </div>
                {step === 2 && (
                    <div className="space-y-4 border-t border-gray-100 bg-light p-5">
                        {option === "other" ? (
                            <>
                                <InputField control={control} name="server" label="SMTP Server" placeholder="Enter SMTP Server" required errors={errors} />
                                <div className="grid gap-4 md:grid-cols-2">
                                    <InputField control={control} name="username" label="SMTP Username" placeholder="Enter SMTP Username" required errors={errors} />
                                    <InputField control={control} name="rate_limit" label="Email API can handle per second" placeholder="Enter Email API can handle per second" errors={errors} />
                                    <InputField control={control} name="from_name" label="From Email Name" placeholder="Enter From Email Name" required errors={errors} />
                                    <InputField control={control} name="email" type="email" label="Email" placeholder="Enter Email" required errors={errors} />
                                </div>
                                <div className="grid gap-4 md:grid-cols-3">
                                    <InputField control={control} name="port" label="Port" required errors={errors} />
                                    <InputField control={control} name="password" type="password" label="Password" placeholder="••••••••" required errors={errors} />
                                    <SelectField control={control} name="security" label="Security Type" options={SECURITY_OPTIONS} errors={errors} />
                                </div>
                                <div>
                                    <InputField control={control} name="notify" label="Notify Additional Users Incase of SMTP Error" placeholder="Enter Notify Additional Users Incase of SMTP Error" errors={errors} />
                                    <p className="-mt-1 text-xs italic text-muted">Note: Please enter the email id&apos;s comma seperated</p>
                                </div>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={connectOauth}
                                    className={classnames(
                                        "inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium",
                                        oauthConnected ? "border border-gray-300 text-gray-700" : "bg-primary text-white hover:bg-primary/90"
                                    )}>
                                    <Mail className="h-4 w-4" />
                                    {oauthConnected ? "Connected" : option === "gmail" ? "Sign in with Google" : "Sign in with Outlook"}
                                </button>
                                {errors.oauth_connected && (
                                    <p className="mt-1.5 text-sm text-danger">{errors.oauth_connected.message}</p>
                                )}
                                <p className="text-sm text-muted">
                                    You need to sign in with {option === "gmail" ? "Google" : "Outlook"} and authorise Nook Pay to send emails on your behalf.
                                </p>
                                <InputField control={control} name="from_name" label="From Email Name" placeholder="Enter From Email Name" required errors={errors} />
                            </>
                        )}
                    </div>
                )}

                <div className="border-t border-gray-100">
                    <StepHeader n={3} title="Test Email" open={step === 3} complete={done.includes(3)} onClick={() => goToStep(3)} />
                </div>
                {step === 3 && (
                    <div className="border-t border-gray-100 bg-light p-5">
                        <InputField control={control} name="test_to" type="email" label="Send Test Mail To" placeholder="Enter Send Test Mail To" required errors={errors} />
                    </div>
                )}

                <div className="flex justify-end gap-3 border-t border-gray-100 bg-white p-4">
                    <button
                        type="button"
                        onClick={previous}
                        disabled={step === 1}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                        <ArrowLeft className="h-4 w-4" />
                        Previous
                    </button>
                    {step < 3 ? (
                        <button
                            type="button"
                            onClick={next}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary/90">
                            Next
                            <ArrowRight className="h-4 w-4" />
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={submit}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary/90">
                            <Send className="h-4 w-4" />
                            Submit
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default BrandSmtp;
